<script setup lang="ts">
import { visibleProjectStages, type ProjectStatus } from '@/utils/project-status'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  status: ProjectStatus
  onHold: boolean
}>()

const { t } = useI18n()

const FILLED_SHARE_OF_RUNNING_STAGE = 44

type StepState = 'done' | 'current' | 'pending'

type TrackState = 'done' | 'running' | 'empty'

const isCancelled = computed(() => props.status === 'cancelled')

const currentIndex = computed(() => visibleProjectStages.findIndex((stage) => stage === props.status))

const stateOf = (index: number): StepState => {
  if (isCancelled.value) {
    return 'pending'
  }

  return index < currentIndex.value ? 'done' : index === currentIndex.value ? 'current' : 'pending'
}

const trackOf = (index: number): TrackState => {
  if (isCancelled.value) {
    return 'empty'
  }

  return index <= currentIndex.value ? 'done' : index === currentIndex.value + 1 ? 'running' : 'empty'
}

const noteOf = (state: StepState): string => {
  if (state === 'current') {
    return props.onHold ? t('note.onHold') : t('note.running')
  }

  return state === 'done' ? t('note.done') : t('note.pending')
}

const steps = computed(() =>
  visibleProjectStages.map((stage, index) => {
    const state = stateOf(index)

    return {
      stage,
      number: index + 1,
      label: t(`enums.projectState.${stage}`),
      note: noteOf(state),
      state,
      track: trackOf(index),
    }
  }),
)

const dotClasses = computed<Record<StepState, string>>(() => ({
  done: 'bg-success-subtle border-success-strong text-success-strong',
  current: props.onHold
    ? 'bg-warning border-warning ring-warning-subtle text-white ring-4'
    : 'bg-action border-action ring-info-subtle text-white ring-4',
  pending: 'bg-surface border-line text-ink-3',
}))

const labelClasses = computed<Record<StepState, string>>(() => ({
  done: 'text-ink-2',
  current: props.onHold ? 'text-warning-strong' : 'text-action',
  pending: 'text-ink-3',
}))
</script>

<template>
  <ol class="mx-auto flex max-w-3xl items-start px-5 pt-4 pb-6" :class="{ 'opacity-50': isCancelled }">
    <li v-for="(step, index) in steps" :key="step.stage" class="relative flex-1 text-center">
      <template v-if="index > 0">
        <span
          v-if="step.track === 'empty'"
          class="fh-track-empty absolute top-[11px] right-[calc(50%+16px)] left-[calc(-50%+16px)] h-0.5"
          aria-hidden="true" />

        <span
          v-else
          class="bg-line-2 absolute top-[10px] right-[calc(50%+16px)] left-[calc(-50%+16px)] h-1 rounded-full"
          aria-hidden="true">
          <span v-if="step.track === 'done'" class="bg-success block h-full rounded-full" aria-hidden="true" />

          <template v-else>
            <span
              class="fh-track-fill absolute inset-y-0 left-0 rounded-full"
              :class="onHold ? 'fh-track-fill--hold' : 'fh-track-fill--run'"
              :style="{ width: `${FILLED_SHARE_OF_RUNNING_STAGE}%` }"
              aria-hidden="true" />

            <span
              v-if="onHold"
              class="fh-track-head bg-warning absolute top-1/2 size-3 -translate-y-1/2 rounded-full border-2 border-white"
              :style="{ left: `calc(${FILLED_SHARE_OF_RUNNING_STAGE}% - 6px)` }"
              aria-hidden="true" />
          </template>
        </span>
      </template>

      <span
        class="relative z-1 mx-auto mb-1.5 flex size-6 items-center justify-center rounded-full border-2 text-[11px] font-bold"
        :class="dotClasses[step.state]">
        {{ step.number }}
      </span>

      <span class="block text-xs font-semibold" :class="labelClasses[step.state]">{{ step.label }}</span>

      <span
        class="mt-0.5 block text-[11px]"
        :class="step.state === 'current' && onHold ? 'text-warning font-semibold' : 'text-ink-3'">
        {{ step.note }}
      </span>
    </li>
  </ol>
</template>

<style scoped>
.fh-track-empty {
  background-image: repeating-linear-gradient(90deg, var(--color-line) 0 5px, transparent 5px 11px);
}

.fh-track-fill {
  overflow: hidden;
}

.fh-track-fill--hold {
  background-color: var(--color-warning);
}

.fh-track-fill--run {
  background-image: var(--gradient-brand);
}

.fh-track-fill::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(115deg, rgb(255 255 255 / 0.6) 0 6px, rgb(255 255 255 / 0) 6px 15px);
  background-size: 21px 100%;
}

/* Angehalten: die Streifen laufen entgegen der Laufrichtung — Bewegung ohne Vorankommen. */
.fh-track-fill--hold::after {
  animation: fh-track-wait 1.6s linear infinite;
}

.fh-track-fill--run::after {
  animation: fh-track-run 1s linear infinite;
}

.fh-track-head {
  animation: fh-track-pulse 2.2s ease-out infinite;
}

@keyframes fh-track-wait {
  to {
    background-position: -21px 0;
  }
}

@keyframes fh-track-run {
  to {
    background-position: 21px 0;
  }
}

@keyframes fh-track-pulse {
  0% {
    box-shadow: 0 0 0 0 rgb(199 122 17 / 0.35);
  }

  70% {
    box-shadow: 0 0 0 9px rgb(199 122 17 / 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgb(199 122 17 / 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fh-track-fill::after,
  .fh-track-head {
    animation: none;
  }
}
</style>

<i18n lang="json">
{
  "de": {
    "note": {
      "onHold": "angehalten",
      "running": "läuft",
      "done": "abgeschlossen",
      "pending": "ausstehend"
    }
  },
  "en": {
    "note": {
      "onHold": "on hold",
      "running": "in progress",
      "done": "completed",
      "pending": "pending"
    }
  }
}
</i18n>
